import React from 'react'
import { Link } from 'react-router-dom'
import { Rocket, Boxes, Puzzle, Code2, BookOpen, Server, FileText, LifeBuoy, ArrowRight, ChevronRight } from 'lucide-react'
import { useCatalog } from '../context/CatalogContext'
import { useSystemStatus } from '../context/SystemStatusContext'
import { useChangelogs } from '../context/ChangelogContext'
import { StatusEntry } from '../types/catalog'

export default function IndexPage() {
  const { entries: changelogs, loading: changelogsLoading } = useChangelogs()
  const { catalog } = useCatalog()
  // API Reference links go to the docs index (which lists every API), not a single spec.
  const apiLink = '/docs#api-specifications'

  const paths = [
    { icon: Rocket, title: 'Quickstart', desc: 'Set up your environment, authenticate, and make your first API call in minutes.', to: '/docs' },
    { icon: Boxes, title: 'Build an App', desc: 'Create apps and templates that appear in the Q-Play App Market and render on screens.', to: '/docs/app-integration-development/guides/building-an-app' },
    { icon: Puzzle, title: 'Build an Integration', desc: 'Fetch live data from external sources with scripts and turn it into on-screen content.', to: '/docs/app-integration-development/guides/building-an-integration' },
    { icon: Code2, title: 'API Reference', desc: 'Browse the OpenAPI specs for the REST, Ajax, Partner and Q-Cal APIs.', to: apiLink },
  ]

  const explore = [
    { icon: BookOpen, title: 'Developer Docs', desc: 'Concepts, quickstarts and how-to guides for building on the platform.', to: '/docs' },
    { icon: Server, title: 'Software & Players', desc: 'Installers, supported player platforms, and the tools you need to deploy.', to: '/software' },
    { icon: FileText, title: 'Examples', desc: 'Real apps walked through end to end - settings, setup, scripts and markup.', to: '/docs/app-integration-development/examples/overview' },
    { icon: LifeBuoy, title: 'Support', desc: 'Get help, report issues, and reach the NordicScreen support team.', to: '/support' },
  ]

  const StatusRow = ({ entry, level }: { entry: StatusEntry; level: number }) => {
    const { statuses } = useSystemStatus()
    const [open, setOpen] = React.useState(level === 0)
    const status = statuses[entry.name]
    const hasChildren = !!(entry.children && entry.children.length > 0)
    const collapsible = hasChildren && level > 0
    const showBadge = !!(entry.url || entry.mapping || hasChildren)

    let state = 'checking'
    let label = 'Checking'
    if (status === 'Online') { state = 'online'; label = 'Online' }
    else if (status === 'Error') { state = 'error'; label = 'Error' }
    else if (status === 'Unknown') { state = 'unknown'; label = 'Unavailable' }

    return (
      <>
        <div
          className={`status-row ${collapsible ? 'clickable' : ''}`}
          style={{ paddingLeft: 6 + level * 18 }}
          onClick={() => collapsible && setOpen(!open)}
        >
          {collapsible ? (
            <ChevronRight size={14} className={`status-chevron ${open ? 'open' : ''}`} />
          ) : level > 0 ? <span className="status-chevron-spacer" /> : null}
          <div className="status-row-text">
            <span className="status-name">{entry.name}</span>
            {entry.title && <span className="status-desc">{entry.title}</span>}
          </div>
          {showBadge && (
            <span className={`status-badge is-${state}`}><span className="status-dot" />{label}</span>
          )}
        </div>
        {hasChildren && open && entry.children!.map((child) => <StatusRow key={child.name} entry={child} level={level + 1} />)}
      </>
    )
  }

  return (
    <main>
      {/* Hero */}
      <section className="section lp-hero">
        <div className="lp-hero-inner">
          <div>
            <span className="lp-eyebrow"><span className="dot" /> NordicScreen Developers</span>
            <h1 className="lp-title">Build with <span className="hl">Q-Play</span></h1>
            <p className="lp-sub">
              Documentation, API specifications and tooling to integrate and extend Q-Play, Q-Cal and more -
              for customers, partners and freelancers.
            </p>
            <div className="lp-cta-row">
              <Link to="/docs" className="btn-primary">Get started <ArrowRight size={16} /></Link>
              <Link to={apiLink} className="btn-secondary">Explore APIs</Link>
            </div>
          </div>
          <div className="lp-hero-side">
            <Link to="/docs/app-integration-development" className="lp-promo">
              <span className="k">Developers</span>
              <h3>Build your own app</h3>
              <p>Create apps and integrations that run on every Q-Play screen.</p>
              <span className="lp-promo-arrow"><ArrowRight size={18} /></span>
            </Link>
            <div className="lp-hero-links">
              <span className="lp-hero-links-label">Quick links</span>
              <Link to="/docs">Getting started</Link>
              <Link to="/docs/authentication">Authentication</Link>
              <Link to={apiLink}>API Reference</Link>
              <Link to="/software/player-software/requirements">Player requirements</Link>
              <Link to="/software">Software &amp; Downloads</Link>
              <Link to="/docs/app-integration-development">Integration &amp; App Dev</Link>
              <Link to="/docs/wiki">Wiki</Link>
              <Link to="/support">Support</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start building */}
      <section className="section lp-section">
        <div className="lp-section-head"><h2 className="lp-section-title">Start building</h2></div>
        <div className="lp-cards lp-cards-4">
          {paths.map(({ icon: Icon, title, desc, to }) => (
            <Link key={title} to={to} className="lp-tile">
              <span className="lp-icon"><Icon size={22} /></span>
              <h3>{title}</h3>
              <p>{desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Explore */}
      <section className="section lp-section">
        <div className="lp-section-head"><h2 className="lp-section-title">Explore the docs</h2></div>
        <div className="lp-cards lp-cards-4">
          {explore.map(({ icon: Icon, title, desc, to }) => (
            <Link key={title} to={to} className="lp-tile">
              <span className="lp-icon"><Icon size={22} /></span>
              <h3>{title}</h3>
              <p>{desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Stay updated */}
      <section className="section lp-section">
        <div className="lp-updates">
          <div className="lp-panel">
            <h3 className="lp-panel-title">Latest changelogs</h3>
            {changelogsLoading && <p className="lp-muted">Updating…</p>}
            {!changelogsLoading && changelogs.length === 0 && <p className="lp-muted">No recent updates.</p>}
            <ul className="lp-changelog">
              {changelogs.slice(0, 5).map((log, idx) => (
                <li key={idx}>
                  <strong>{log.system}</strong> <em>· {log.date}</em>
                  <ul>{log.items.map((i, j) => <li key={j}>{i}</li>)}</ul>
                </li>
              ))}
            </ul>
          </div>
          <div className="lp-panel">
            <h3 className="lp-panel-title">System status</h3>
            {catalog?.status ? (
              <div className="lp-status">
                {catalog.status.filter((e) => e.enabled !== false).map((entry) => <StatusRow key={entry.name} entry={entry} level={0} />)}
              </div>
            ) : (
              <p className="lp-muted">No status information configured.</p>
            )}
          </div>
        </div>
      </section>
    </main>
  )
}
